import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import EmptyState from '@atlaskit/empty-state';
import Spinner from '@atlaskit/spinner';
import Flag, { FlagGroup } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/core/information';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import SearchFailedImage from '#assets/search-failed.svg';
import { useMcpContext } from '../McpContextProvider';
import { useSelectedWorkspace } from '../../../Flow/ConsentContextProvider';
import { useMcpBranding } from '../mcpTerminology';
import { mcpBannerMessages, mcpPermissionsMessages } from './messages';
import McpIntentPermissionCard from './McpIntentPermissionCard';
import { useUnselectedPermissionsWarning } from './useUnselectedPermissionsWarning';
import { MCP_PERMISSIONS_MANAGEMENT_DOC_URL as MCP_PERMISSIONS_MANAGEMENT_DOC } from '../mcpUrls';
import { MCP_POLICY_STATUS } from '../domain/policyState';

const stackStyle = xcss({ width: '100%', maxWidth: '100%' });
const headerRowStyle = xcss({ minHeight: 'size.300' });

const flagContainerStyle = xcss({
  position: 'fixed',
  bottom: 'space.400',
  left: 'space.400',
  // @ts-expect-error static z-index
  zIndex: 100,
  width: '400px',
  maxWidth: '90vw',
  backgroundColor: 'color.background.warning',
  borderRadius: 'radius.small',
  boxShadow: 'elevation.shadow.overlay',
});

const McpPermissions: FunctionComponent = () => {
  const { selectedWorkspace } = useSelectedWorkspace();
  const {
    mcpPolicyStatus,
    mcpByIntent,
    mcpSelectedToolsetNames,
    toggleMcpToolset: toggleToolset,
    selectedTrelloWorkspaces,
    selectedBitbucketWorkspaces,
    inboxPlannerScopesDisabled,
    isTrelloMultiWorkspaceSelectorEnabled,
    availableTrelloWorkspaces,
    hasTrelloInboxPlannerScopes,
    scopeClassification,
  } = useMcpContext();

  const { formatMessage } = useIntl();
  const { product, productUnit } = useMcpBranding();
  const [flagDismissed, setFlagDismissed] = useState(false);
  const selectedSet = mcpSelectedToolsetNames ?? new Set<string>();
  const hasUnselected = useUnselectedPermissionsWarning({
    selectedWorkspace,
    mcpByIntent,
    selectedToolsetNames: selectedSet,
  });

  useEffect(() => {
    setFlagDismissed(false);
  }, [selectedWorkspace]);

  // Inbox and Planner are account level, so a member with no workspaces at all
  // can still grant them — permissions stay visible without a selection.
  const isInboxPlannerOnly =
    scopeClassification.hasTrello &&
    availableTrelloWorkspaces.length === 0 &&
    hasTrelloInboxPlannerScopes;

  // Don't show permissions until the user has selected a workspace, either an
  // Atlassian site, a Trello workspace, or a Bitbucket workspace.
  if (
    !selectedWorkspace &&
    selectedTrelloWorkspaces.length === 0 &&
    selectedBitbucketWorkspaces.length === 0 &&
    !isInboxPlannerOnly
  )
    return null;

  if (
    mcpPolicyStatus === MCP_POLICY_STATUS.loading ||
    mcpPolicyStatus === MCP_POLICY_STATUS.idle
  ) {
    return (
      <Stack xcss={stackStyle} space="space.400">
        <Inline alignBlock="center" xcss={headerRowStyle}>
          <Heading size="small">
            <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
          </Heading>
        </Inline>
        <Spinner size="large" label="Loading permissions" />
      </Stack>
    );
  }

  if (mcpPolicyStatus === MCP_POLICY_STATUS.error) return null;

  if (mcpByIntent.length === 0) {
    return (
      <EmptyState
        header={formatMessage(
          isTrelloMultiWorkspaceSelectorEnabled
            ? mcpPermissionsMessages.emptyStateHeadingMultiple
            : mcpPermissionsMessages.emptyStateHeading,
          { productUnit },
        )}
        description={
          <FormattedMessage
            {...mcpPermissionsMessages.emptyStateDescription}
            values={{
              howPermissionsLink: (text) => (
                <Link
                  href={MCP_PERMISSIONS_MANAGEMENT_DOC}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={SearchFailedImage}
      />
    );
  }

  return (
    <Stack xcss={stackStyle} space="space.100">
      {isInboxPlannerOnly && (
        <SectionMessage appearance="information">
          <Text as="p">
            <FormattedMessage
              {...mcpBannerMessages.noWorkspacesInboxPlannerMessage}
            />
          </Text>
        </SectionMessage>
      )}
      <Inline
        alignBlock="center"
        spread="space-between"
        space="space.200"
        shouldWrap
        xcss={headerRowStyle}
      >
        <Heading size="small">
          <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
        </Heading>
      </Inline>
      {hasUnselected && !flagDismissed && (
        <Box xcss={flagContainerStyle}>
          <FlagGroup onDismissed={() => setFlagDismissed(true)}>
            <Flag
              id="mcp-permission-warning"
              icon={
                <InfoIcon label="" color={token('color.icon.information')} />
              }
              title={formatMessage(mcpBannerMessages.editWarningTitle, {
                product,
              })}
              description={formatMessage(mcpBannerMessages.editWarningBody, {
                product,
              })}
              actions={[
                {
                  content: formatMessage(mcpBannerMessages.editWarningDismiss),
                  href: 'javascript:void(0)',
                  onClick: (e) => {
                    e.preventDefault();
                    setFlagDismissed(true);
                  },
                },
              ]}
            />
          </FlagGroup>
        </Box>
      )}
      <Stack space="space.200">
        {mcpByIntent.map((group) => (
          <McpIntentPermissionCard
            key={group.id}
            group={group}
            selectedToolsetNames={selectedSet}
            onToggleToolset={(name, checked) => toggleToolset(name, checked)}
            onToggleSummaryCard={(checked) =>
              group.toolsets.forEach((t) => toggleToolset(t.name, checked))
            }
          />
        ))}
      </Stack>
      {inboxPlannerScopesDisabled && (
        <SectionMessage appearance="information">
          <Text as="p">
            <FormattedMessage
              {...(isTrelloMultiWorkspaceSelectorEnabled
                ? mcpBannerMessages.inboxPlannerDisabledMultipleMessage
                : mcpBannerMessages.inboxPlannerDisabledMessage)}
            />
          </Text>
        </SectionMessage>
      )}
    </Stack>
  );
};

export default McpPermissions;
